import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RespiratoryInitialContext } from "./RespiratoryTherapistInitialAssessmentProvider";
import {
  respiratoryInspectionAPI,
  updateRespiratoryInspectionAPI,
} from "../../../../services/respiratory.services";
import { Button, Col, Form, message, Modal, Row, Tabs } from "antd";
import ClinicalImpressionForm from "../../InspectionForms/ClinicalImpressionForm/ClinicalImpressionForm";
import RespiratoryTherapistChartAssessment from "../../InspectionForms/ChartAssessment/RespiratoryTherapistChartAssessment";
import RespiratoryPatientAssessment from "../../InspectionForms/PatientAssessment/RespiratoryPatientAssessment";
import RespiratoryTherapistTreatmentPlan from "../../InspectionForms/TreatmentPlan/RespiratoryTherapistTreatmentPlan";
import RespiratorySpecialInstruction from "../../InspectionForms/SpecialAreas/RespiratorySpecialInstruction";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";

const RespiratoryTherapistInitialInspectionForm = ({
  setVisible,
  refreshPage,
  initialRefresh,
  editId,
  patientData,
  setConfirmModal,
  activeTab,
  setActiveTab,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [clinicalForm] = Form.useForm();
  const [chartForm] = Form.useForm();
  const [patientForm] = Form.useForm();
  const [treatmentForm] = Form.useForm();
  const [specialForm] = Form.useForm();
  const [buttonPressed, setButtonPressed] = useState(false);
  const { formData } = useContext(RespiratoryInitialContext);
  const [isDirty, setIsDirty] = useState(false);

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "Clinical" : "Clinical Impression ",
      },
      {
        key: "2",
        label: isMobile ? "Chart" : "Chart Assessment",
      },
      {
        key: "3",
        label: isMobile ? "Patient" : "Patient Assessment",
      },
      {
        key: "4",
        label: isMobile ? "Treatment" : "Treatment Plan",
      },
      {
        key: "5",
        label: isMobile ? "Special" : "Special Instructions",
      },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId) {
        await updateRespiratoryInspectionAPI(editId, values);
        initialRefresh();
      } else {
        await respiratoryInspectionAPI(values);
        refreshPage();
        initialRefresh();
      }

      message.success(`${flag} submitted successfully`);

      if (buttonPressed) {
        if (activeTab === "5") {
          setVisible(false);
        } else {
          const nextTab = (parseInt(activeTab) + 1).toString();
          setActiveTab(nextTab);
        }
        setButtonPressed(false);
      }
      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onClinicalSubmit = (values) => {
    handleSubmit(
      { clinical_diagnosis: values, admission_id: params?.patient },
      "Clinical Impression/ Diagnosis"
    );
  };
  const onChartSubmit = (values) => {
    handleSubmit(
      {
        chart_assessment: { ...values },
        admission_id: params?.patient,
      },
      "Chart Assessment"
    );
  };
  const onPatientSubmit = (values) => {
    handleSubmit(
      {
        patient_assessment: {
          ...values,
        },
        admission_id: params?.patient,
      },
      "Patient Assessment"
    );
  };
  const onTreatmentSubmit = (values) => {
    handleSubmit(
      {
        treatment_plan: values,
        admission_id: params?.patient,
      },
      "Treatment Plan"
    );
  };
  const onSpecialSubmit = (values) => {
    handleSubmit(
      {
        special_instruction: values,
        admission_id: params?.patient,
      },
      "Special Instructions"
    );
  };

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return clinicalForm.submit();
      case "2":
        return chartForm.submit();
      case "3":
        return patientForm.submit();
      case "4":
        return treatmentForm.submit();
      case "5":
        return specialForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      Modal.confirm({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Are you sure you want to save these changes?",
        onOk: () => {
          switch (activeTab) {
            case "1":
              clinicalForm.validateFields().then((values) => {
                setIsDirty(false);
                onClinicalSubmit(values);
                setActiveTab(key);
              });
              break;
            case "2":
              chartForm.validateFields().then((values) => {
                setIsDirty(false);
                onChartSubmit(values);
                setActiveTab(key);
              });
              break;
            case "3":
              patientForm.validateFields().then((values) => {
                setIsDirty(false);
                onPatientSubmit(values);
                setActiveTab(key);
              });
              break;
            case "4":
              treatmentForm.validateFields().then((values) => {
                setIsDirty(false);
                onTreatmentSubmit(values);
                setActiveTab(key);
              });
              break;
            case "5":
              specialForm.validateFields().then((values) => {
                setIsDirty(false);
                onSpecialSubmit(values);
                setActiveTab(key);
              });
              break;
            default:
              setActiveTab(key);
          }
        },
        onCancel: () => {
          setActiveTab(key);
          setIsDirty(false);
        },
      });
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  return (
    <div className="initial-inspection-form-wrapper">
      <div className="ia-form-tabs">
        <Row>
          <Col
            xs={24}
            sm={24}
            md={{ span: 23, offset: 1 }}
            lg={{ span: 21, offset: 3 }}
            xl={{ span: 19, offset: 5 }}
            xxl={{ span: 18, offset: 6 }}
          >
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={handleTabChange}
            />
          </Col>
        </Row>
      </div>
      <div className="ia-form-panel">
        <div className="ia-form-body" ref={formBodyRef}>
          {activeTab === "1" && (
            <Form
              id="clinical_diagnosis-form"
              form={clinicalForm}
              layout="vertical"
              onFinish={onClinicalSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <ClinicalImpressionForm
                form={clinicalForm}
                context={RespiratoryInitialContext}
                editId={editId}
                patientData={patientData}
              />
            </Form>
          )}
          {activeTab === "2" && (
            <Form
              id="chart-form"
              form={chartForm}
              layout="vertical"
              onFinish={onChartSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <RespiratoryTherapistChartAssessment form={chartForm} />
            </Form>
          )}
          {activeTab === "3" && (
            <Form
              id="patient-form"
              form={patientForm}
              layout="vertical"
              onFinish={onPatientSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <RespiratoryPatientAssessment
                form={patientForm}
                context={RespiratoryInitialContext}
              />
            </Form>
          )}
          {activeTab === "4" && (
            <Form
              id="treatment-form"
              form={treatmentForm}
              layout="vertical"
              onFinish={onTreatmentSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <RespiratoryTherapistTreatmentPlan
                form={treatmentForm}
                context={RespiratoryInitialContext}
              />
            </Form>
          )}
          {activeTab === "5" && (
            <Form
              id="special-instruction"
              form={specialForm}
              layout="vertical"
              onFinish={onSpecialSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <RespiratorySpecialInstruction form={specialForm} />
            </Form>
          )}
        </div>
        <div className="ia-form-footer">
          <Button
            onClick={() => {
              getFormKey();
              setButtonPressed(true);
              document
                .querySelector(".initial-inspection-form-wrapper")
                ?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
            }}
            type="primary"
            className={btnStyles.mdBtn}
            style={{ fontWeight: 500 }}
          >
            {activeTab === "5" ? "Done" : " Save & Proceed"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RespiratoryTherapistInitialInspectionForm;
